/**
 * @module routes.Sessions.AllCoachesPrivateSession.seedFacility
 */

interface FacilityLike {
  id?: string;
}

/**
 * Which facility the booking page should open on when it seeds a fresh booking.
 *
 * The page re-seeds every time it mounts, and it is where the back arrow of every
 * screen below it leads — the coach's page, their calendar. Seeding took
 * `facilities[0]`, the first facility the athlete belongs to, so an athlete who
 * belongs to more than one picked a coach, pressed back, and arrived at a
 * different facility's page than the one they had been looking at.
 *
 * So the facility they are already on wins, when they still belong to it. The
 * shape is `defaultSelection`'s `coachData` — a facility and no coach, which is
 * how that helper is told to scope a default to one facility.
 *
 * `null` — meaning "choose for me" — for the first booking of a session, for a
 * facility they have since left, and for a page opened cold.
 */
export const seedFacility = (
  facilities: FacilityLike[] | undefined,
  currentFacilityId: string | undefined,
): { facilityId: string } | null => {
  if (!currentFacilityId) {
    return null;
  }

  // `defaultSelection` looks the facility up by id and has no fallback for one it
  // cannot find — it would seed the booking with no facility at all.
  const stillTheirs = (facilities || []).some(
    (facility) => facility?.id === currentFacilityId,
  );

  return stillTheirs ? { facilityId: currentFacilityId } : null;
};

export default seedFacility;
